import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { canAccess } from "../../../utils/permissions";

const TestPackageTable = ({
  data,
  loading,
  onEdit,
  onDelete,
  onRestore,
  type = "test",
  searchTerm,
  // New pagination props
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
}) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  // Calculate pagination range
  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-6 text-center">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Loading {type === "test" ? "tests" : "packages"}...
          </p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-6 text-center">
          <div className="flex flex-col items-center justify-center">
            <Icon name="Package" size={32} className="text-gray-400 mb-2" />
            <p className="text-gray-500 font-medium text-xs">
              No {type === "test" ? "tests" : "packages"} found
            </p>
            <p className="text-gray-400 text-xs mt-0.5">
              {searchTerm
                ? "Try adjusting your search"
                : `No ${
                    type === "test" ? "tests" : "packages"
                  } have been added yet`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-4">
        {/* Table Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {type === "test" ? "Individual Tests" : "Health Packages"}
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} items
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-8">
                  SL
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Item Info
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Also Know As
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Report Time
                </th>
                {type === "test" && (
                  <>
                   <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      No of Parameter
                    </th>
                  </>
                )}
                {type === "package" && (
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tests
                  </th>
                )}
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Order
                </th>

                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  // onClick={() =>
                  //   navigate(`/test-parameters/${item.id}`, {
                  //     state: { testName: item.name },
                  //   })
                  // }
                  // onKeyDown={(e) => {
                  //   if (e.key === "Enter" || e.key === " ") {
                  //     navigate(`/test-parameters/${item.id}`, {
                  //       state: { testName: item.name },
                  //     });
                  //   }
                  // }}
                  className={`cursor-pointer transition-colors duration-150 ${
                    item.status === "archived"
                      ? "bg-amber-50 hover:bg-amber-100 border-l-4 border-amber-400"
                      : "hover:bg-gray-50"
                  }`}>
                  {/* Serial Number */}
                  <td className="px-3 py-2 text-gray-500 font-medium">
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-md flex items-center justify-center overflow-hidden">
                        {item.imgUrl ? (
                          <img
                            src={item.imgUrl}
                            alt={item.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Icon
                            name="Package"
                            size={16}
                            className="text-blue-600"
                          />
                        )}
                      </div>
                      <div className="ml-2">
                        <div
                          className={`text-xs font-medium flex items-center gap-1 ${
                            item.status === "archived" ? "text-gray-500 line-through" : "text-gray-900"
                          }`}>
                          {item.name}
                          {item.status === "archived" && (
                            <span
                              title="Archived — hidden from app & new orders; past bills/bookings intact"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wide no-underline">
                              <Icon name="Archive" size={10} />
                              Archived
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {item.description}
                          </div>
                        )}
                        {type === "test" && item.testType && (
                          <div className="text-xs text-blue-600">
                            {item.testType}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium `}>
                      {item.alsoKnowAs || "-"}
                    </span>
                  </td>
                  {/* Pricing */}
                  <td className="px-3 py-2">
                    <div className="text-xs text-gray-900">
                      <span className="line-through text-gray-400 mr-1">
                        ₹{item.actualPrice}
                      </span>
                      <span className="font-semibold">
                        ₹{item.offerPrice || item.actualPrice}
                      </span>
                    </div>
                    <div className="text-xs text-green-600">
                      {item.discount || 0}% off
                    </div>
                  </td>

                  {/* Report Time */}
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                    {item.reportWithin} {item.reportUnit}
                  </td>

                  {/* Test Specific Columns */}
                  {type === "test" && (
                    <>
                     <td className="px-3 py-2 text-xs text-gray-500">
                        {item.departmentItem?.name || "0"}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {item.category?.name || `Cat: ${item.categoryId}`}
                        {item.subCategory && (
                          <>
                            <br />
                            <span className="text-gray-400">
                              Sub:{" "}
                              {item.subCategory?.name || item.subCategoryId}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {item._count?.parameters || "0"}
                      </td>
                    </>
                  )}

                  {type === "package" && (
                    <td className="px-3 py-2 text-xs text-gray-700 font-semibold">
                      <ul className="list-disc ml-4">
                        {item.tests.map((test, index) => (
                          <li key={index}>{test.name}</li>
                        ))}
                      </ul>
                    </td>
                  )}
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {item.sortOrder || "0"}
                  </td>
                  {/* Actions */}
                  <td className="px-3 py-2">
                    <div className="flex justify-end space-x-1">
                      {/* View Parameters Button - Only for tests */}
                      {type === "test" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Eye"
                          title="View Parameters"
                          className={`
                            text-green-600 border border-green-200 text-xs px-2 py-1
                            hover:text-green-800 hover:border-green-300
                          `}
                          onClick={() =>
                            navigate(`/test-parameters/${item.id}`, {
                              state: { testName: item.name },
                            })
                          }
                        />
                      )}

                      {/* Edit Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        disabled={!canAccess(currentUser, "canEdit")}
                        className={`
                          text-blue-600 border border-blue-200 text-xs px-2 py-1
                          ${
                            !canAccess(currentUser, "canEdit")
                              ? "opacity-40 cursor-not-allowed hover:text-blue-600 hover:border-blue-200"
                              : "hover:text-blue-800 hover:border-blue-300"
                          }
                        `}
                        onClick={() =>
                          canAccess(currentUser, "canEdit") && onEdit(item)
                        }
                      />

                      {/* Delete (tests) / Archive (packages) / Restore (archived packages) */}
                      {item.status === "archived" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="RotateCcw"
                          title={type === "package" ? "Restore package" : "Restore test"}
                          disabled={!canAccess(currentUser, "canDelete")}
                          className={`
                            text-emerald-600 border border-emerald-200 text-xs px-2 py-1
                            ${
                              !canAccess(currentUser, "canDelete")
                                ? "opacity-40 cursor-not-allowed"
                                : "hover:text-emerald-800 hover:border-emerald-300"
                            }
                          `}
                          onClick={() =>
                            canAccess(currentUser, "canDelete") &&
                            onRestore?.(item.id)
                          }
                        />
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Archive"
                          title={
                            type === "package"
                              ? "Archive package (bills & bookings are kept)"
                              : "Archive test (results, bills & bookings are kept)"
                          }
                          disabled={!canAccess(currentUser, "canDelete")}
                          className={`
                            text-red-600 border border-red-200 text-xs px-2 py-1
                            ${
                              !canAccess(currentUser, "canDelete")
                                ? "opacity-40 cursor-not-allowed hover:text-red-600 hover:border-red-200"
                                : "hover:text-red-800 hover:border-red-300"
                            }
                          `}
                          onClick={() =>
                            canAccess(currentUser, "canDelete") &&
                            onDelete(item.id)
                          }
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
              entries
            </div>

            <div className="flex items-center space-x-1">
              {/* Previous Button */}
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-2 py-1.5 rounded-md text-xs font-medium ${
                  currentPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-100"
                }`}>
                Previous
              </button>

              {/* Page Numbers */}
              {getPaginationRange().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === "number" && onPageChange(page)}
                  className={`w-7 h-7 flex items-center justify-center rounded-md text-xs font-medium ${
                    page === currentPage
                      ? "bg-blue-600 text-white"
                      : typeof page === "number"
                        ? "text-gray-700 hover:bg-gray-100"
                        : "text-gray-400 cursor-default"
                  }`}
                  disabled={typeof page !== "number"}>
                  {page}
                </button>
              ))}

              {/* Next Button */}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-2 py-1.5 rounded-md text-xs font-medium ${
                  currentPage === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-100"
                }`}>
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TestPackageTable;
